import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { ThemeService } from '../services/theme';
import { V3_ONBOARDING_STORAGE_KEY } from '../v3-onboarding-modal/v3-onboarding-modal';
import { environment } from '../../environments/environment';

export const V3_LOADING_SCREEN_MIN_DURATION_MS = 2500;

// `?v3LoaderMinMs=<ms>` overrides the floor on non-production builds only (E2E/QA), like `?v3=`.
const V3_LOADER_MIN_MS_PARAM = 'v3LoaderMinMs';

export function readLoadingScreenMinDurationMs(
  search: string = window.location.search,
  production: boolean = environment.production,
): number {
  if (production) return V3_LOADING_SCREEN_MIN_DURATION_MS;
  const raw = new URLSearchParams(search).get(V3_LOADER_MIN_MS_PARAM);
  if (!raw) return V3_LOADING_SCREEN_MIN_DURATION_MS;
  const ms = Number(raw);
  return Number.isInteger(ms) && ms >= 0 ? ms : V3_LOADING_SCREEN_MIN_DURATION_MS;
}

// AI-617: no floor while the onboarding modal is still due (it covers the page anyway), nor
// for a re-opening within two minutes of the last floor that actually ran.
export const V3_LOADING_SCREEN_SHOWN_AT_KEY = 'v3_loading_screen_shown_at';
export const V3_LOADING_SCREEN_REPEAT_WINDOW_MS = 2 * 60 * 1000;

export function shouldSkipLoadingScreenFloor(now: number = Date.now()): boolean {
  if (localStorage.getItem(V3_ONBOARDING_STORAGE_KEY) !== '1') return true;
  const elapsed = now - Number(localStorage.getItem(V3_LOADING_SCREEN_SHOWN_AT_KEY));
  return elapsed >= 0 && elapsed < V3_LOADING_SCREEN_REPEAT_WINDOW_MS;
}

// Space before the ellipsis is Duden-correct; no-break so the dots never wrap alone.
export const V3_LOADING_SCREEN_LABELS: readonly string[] = [
  'Prüfe die Kennzahlen von FactSet\u00a0…',
  'Suche die Kursziele der besten Analysten\u00a0…',
  'Berechne die Fundamental Scores\u00a0…',
  'Ordne die News von Reuters ein\u00a0…',
  'Vergleiche die Top-Wettbewerber\u00a0…',
  'Prüfe die Trefferquote aller Analysten\u00a0…',
  'Werte die letzten Geschäftsberichte aus\u00a0…',
];

export function pickRandomLoadingLabel(
  labels: readonly string[] = V3_LOADING_SCREEN_LABELS,
): string {
  return labels[Math.floor(Math.random() * labels.length)];
}

@Component({
  selector: 'app-v3-loading-screen',
  standalone: true,
  imports: [LottieComponent],
  templateUrl: './v3-loading-screen.html',
  styleUrl: './v3-loading-screen.scss',
})
export class V3LoadingScreen implements OnInit, OnDestroy {
  // Core listens for this to release its showLoader() gate — see core.ts.
  @Output() minDurationElapsed = new EventEmitter<void>();

  private readonly themeService = inject(ThemeService);
  private timer?: ReturnType<typeof setTimeout>;

  readonly label = pickRandomLoadingLabel();

  // Not a getter — ng-lottie reloads the animation whenever it sees a new object reference.
  readonly options: AnimationOptions = {
    path: this.themeService.isDark() ? '/v3/lottie/darkLoader.json' : '/v3/lottie/lightLoader.json',
    autoplay: true,
    loop: true,
  };

  ngOnInit(): void {
    const minMs = shouldSkipLoadingScreenFloor() ? 0 : readLoadingScreenMinDurationMs();
    // Still a timer when skipped — a synchronous emit would land mid change detection.
    this.timer = setTimeout(() => {
      // Recorded on completion only: a floor cut short (error page, tab closed) doesn't count.
      if (minMs > 0) localStorage.setItem(V3_LOADING_SCREEN_SHOWN_AT_KEY, String(Date.now()));
      this.minDurationElapsed.emit();
    }, minMs);
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
  }
}
